import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, GiftIcon, HelpCircleIcon, QuoteIcon, ShieldIcon, TrophyIcon, XIcon } from 'lucide-react';
import { bonuses, developedTestimonials, faqs, modules, pains, purchaseUrl, testimonials } from '../data/content';
import { Button } from './ui/Button';
const collapseTransition = {
  duration: 0.22,
  ease: [0.23, 1, 0.32, 1] as const
};
function Eyebrow({
  children,
  danger = false
}: {
  children: React.ReactNode;
  danger?: boolean;
}) {
  return <p className={`mb-3 text-center text-[11px] font-extrabold uppercase tracking-[.16em] md:text-sm ${danger ? 'text-red-500' : 'text-accent-dark'}`} data-id="element-17" data-mp="1no478ia3clrt">
      {children}
    </p>;
}
function SectionTitle({
  children
}: {
  children: React.ReactNode;
}) {
  return <h2 className="mx-auto max-w-3xl text-center text-2xl font-black leading-tight text-ink md:text-4xl" data-id="element-18" data-mp="2z2zxz2m0idoq">
      {children}
    </h2>;
}
export function Testimonials() {
  return <section className="mx-auto max-w-6xl px-4 py-12 md:py-14" data-id="element-19" data-mp="1bp591v6f0240">
      <Eyebrow data-id="element-20" data-mp="16q2i1en9rwf1">⭐ 4,9/5 · +200 corredores</Eyebrow>
      <SectionTitle data-id="element-21" data-mp="16rdltegpt465">
        Lo que dicen los que <span className="text-accent" data-id="element-22" data-mp="12oh6bwmh1o6f">ya lo están usando</span>
      </SectionTitle>
      <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-23" data-mp="xpw4xtvhgyge">
        {testimonials.map(item => <article key={item.name} className="relative flex flex-col rounded-2xl border border-line bg-white p-6 shadow-sm" data-id="element-24" data-mp="sqsxy3vy0i5s">
            <QuoteIcon className="absolute right-5 top-5 h-7 w-7 text-accent/20" data-id="element-25" data-mp="nyjff8x5qkdj" />
            <div className="mb-3 text-sm text-gold" data-id="element-26" data-mp="j218q3i9obyk">⭐⭐⭐⭐⭐</div>
            <p className="mb-5 flex-1 text-base font-semibold italic leading-relaxed text-slate-700" data-id="element-27" data-mp="ezo63nytgu60">
              “{item.text}”
            </p>
            <div className="mt-auto flex items-center gap-3 border-t border-line pt-4" data-id="element-28" data-mp="9zs2hpu174qz">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-accent/30 bg-canvas text-sm font-black text-primary" data-id="element-29" data-mp="52jidjpboqn3">
                {item.initials}
              </span>
              <div data-id="element-30" data-mp="e0lk1opbnmm">
                <p className="text-sm font-extrabold text-ink" data-id="element-31" data-mp="acr90bfmx6u">{item.name}</p>
                <p className="text-xs text-slate-500" data-id="element-32" data-mp="3hmnk2lpv6cl8">📍 {item.place}</p>
              </div>
            </div>
          </article>)}
      </div>
    </section>;
}
export function Pain() {
  return <section className="border-y border-line bg-white px-4 py-12 md:py-14" data-id="element-33" data-mp="e4vvlhaxsars">
      <div className="mx-auto max-w-4xl" data-id="element-34" data-mp="j1vu975gib8v">
        <Eyebrow danger data-id="element-35" data-mp="j51lot1k5p7r">😤 La realidad</Eyebrow>
        <SectionTitle data-id="element-36" data-mp="4d9bevgmf1cw">
          ❓ ¿Cuántas carreras más vas a <span className="text-accent" data-id="element-37" data-mp="9ddxd10asoai">improvisar?</span>
        </SectionTitle>
        <div className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-38" data-mp="9bav9ilvf4pf">
          {pains.map(pain => <div key={pain} className="flex items-start gap-3 rounded-xl border border-line bg-canvas p-4" data-id="element-39" data-mp="x1nalejg43q1">
              <XIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" data-id="element-40" data-mp="2mjkc1uzmm07e" />
              <span className="text-sm font-semibold text-slate-700 md:text-base" data-id="element-41" data-mp="2hl0mlkltq6aa">{pain}</span>
            </div>)}
        </div>
        <p className="mt-8 text-center text-lg font-bold text-slate-700 md:text-2xl" data-id="element-42" data-mp="2w8vfb0zu0and">
          No es falta de experiencia.{' '}
          <span className="font-black text-accent" data-id="element-43" data-mp="2rbltdvbnih4a">Es falta de un SISTEMA.</span>
        </p>
      </div>
    </section>;
}
export function ProductOverview() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-4xl px-4 py-12 md:py-14" data-id="element-44" data-mp="1yo96tl5hq9az">
      <Eyebrow data-id="element-45" data-mp="2cmz0srx0jsv4">
        <span className="inline-flex items-center gap-2" data-id="element-46" data-mp="2cnh6sg9h9lg8">
          <TrophyIcon className="h-4 w-4" data-id="element-47" data-mp="28jsz7xkuipuz" /> Qué incluye el sistema
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-48" data-mp="3o0jojenbch1z">
        6 tomos. <span className="text-accent" data-id="element-49" data-mp="3nzrqzya5o992">De 0K a 21K.</span>
      </SectionTitle>
      <p className="mt-3 text-center font-semibold text-slate-600" data-id="element-50" data-mp="3k098ta9pyc9x">
        Más de 450 páginas de running, adaptaciones y casos reales listos para implementar.
      </p>

      <div className="mt-8 space-y-3" data-id="element-51" data-mp="n5vhbghyhohj">
        {modules.map((mod, index) => <div key={mod.name} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-150 ease-out ${open === index ? 'border-accent shadow-lg shadow-slate-200' : 'border-line'}`} data-id="element-52" data-mp="n7y5rw3gn9k5" data-mpi={index}>
            <button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-6" aria-expanded={open === index} onClick={() => setOpen(open === index ? -1 : index)} data-id="element-53" data-mp="123xkvqozm9ys">
              <span className="flex items-center gap-3" data-id="element-54" data-mp="1axj01b52dt8q">
                <span className="text-2xl" data-id="element-55" data-mp="1fusp2pojk2w1">{mod.icon}</span>
                <span data-id="element-56" data-mp="1ktm0mck9pjtk">
                  <b className="block text-sm font-black uppercase text-ink md:text-base" data-id="element-57" data-mp="1ku5ijt9x487l">
                    {mod.name}
                  </b>
                  <small className="font-bold text-accent-dark" data-id="element-58" data-mp="1psymatzekkuk">{mod.period}</small>
                </span>
              </span>
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-accent transition-transform duration-200 ease-out ${open === index ? 'rotate-180' : ''}`} data-id="element-59" data-mp="3a8emz0lg305h" />
            </button>
            <AnimatePresence initial={false} data-id="element-60" data-mp="2vdi6k03hf6hb">
              {open === index && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={collapseTransition} data-id="element-61" data-mp="2vfuwazfhel4x">
                  <ul className="space-y-2 border-t border-line px-5 py-4 text-sm font-semibold text-slate-600 md:px-6" data-id="element-62" data-mp="3ja4mktphd5ec">
                    {mod.content.map(line => <li key={line} className="flex gap-2" data-id="element-63" data-mp="fmvzvge2270t">
                        <span className="text-accent" data-id="element-64" data-mp="fofb2skutrvq">→</span>
                        {line}
                      </li>)}
                  </ul>
                </motion.div>}
            </AnimatePresence>
          </div>)}
      </div>
    </section>;
}
export function BonusesSection() {
  return <section className="border-y border-line bg-white px-4 py-12 md:py-14" data-id="element-65" data-mp="1ve6ky0ep38yf">
      <div className="mx-auto max-w-6xl" data-id="element-66" data-mp="20crj7uadzik8">
        <Eyebrow data-id="element-67" data-mp="20bpzl1fb1nqw">
          <span className="inline-flex items-center gap-2" data-id="element-68" data-mp="20b6mpz4ihpjq">
            <GiftIcon className="h-4 w-4" data-id="element-69" data-mp="1mcqkakdoml7e" /> 5 bonos siempre incluidos
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-70" data-mp="1qetwxcc37vu9">
          Además te llevás <span className="text-accent" data-id="element-71" data-mp="1qfwoznl5c4br">5 bonos GRATIS</span>
        </SectionTitle>
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-72" data-mp="1cgdggtofaqyx">
          {bonuses.map(bonus => <article key={bonus.number} className="flex flex-col rounded-2xl border border-line bg-canvas p-5 shadow-sm" data-id="element-73" data-mp="1he5kgm2ce3jh">
              <div className="flex gap-3" data-id="element-74" data-mp="3bs6durf4hyc6">
                <span className="text-3xl" data-id="element-75" data-mp="3bpu0krkb8c5g">{bonus.icon}</span>
                <div data-id="element-76" data-mp="105mgn6bisa35">
                  <small className="font-extrabold uppercase tracking-wide text-accent-dark" data-id="element-77" data-mp="104uknue0gacc">
                    Bono {bonus.number}
                  </small>
                  <h3 className="text-base font-black uppercase leading-tight text-ink" data-id="element-78" data-mp="19umicxhepjl1">
                    {bonus.name}
                  </h3>
                </div>
              </div>
              <p className="mt-4 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-79" data-mp="1iussqaht63fz">
                {bonus.description}
              </p>
              <ul className="my-4 flex-1 space-y-1.5 text-sm font-semibold leading-snug text-slate-600" data-id="element-80" data-mp="1dvg5vpqxswde">
                {bonus.items.map(item => <li key={item} className="flex gap-2" data-id="element-81" data-mp="1nuf6cl1jtlpb">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" data-id="element-82" data-mp="1nux8tzet0w5o" />
                    {item}
                  </li>)}
              </ul>
              <div className="mt-auto flex items-center border-t border-line pt-4 text-sm font-extrabold" data-id="element-83" data-mp="3pmk4wfdjzh90">
                <span className="text-slate-400 line-through" data-id="element-84" data-mp="33rbeibdghlj7">{bonus.value} valor</span>
                <span className="ml-auto rounded-full bg-accent px-3 py-1 text-white" data-id="element-85" data-mp="38qo619f9vgs4">
                  GRATIS HOY
                </span>
              </div>
            </article>)}
        </div>
        <p className="mt-7 text-center text-base font-bold text-slate-600 md:text-lg" data-id="element-86" data-mp="3ig7le53s0si8">
          Valor total de bonos: <s className="text-slate-400" data-id="element-87" data-mp="2kusdyagpx5y9">$58.950 ARS</s> →{' '}
          <span className="font-black text-accent" data-id="element-88" data-mp="2psbqn5xrpao3">incluidos gratis hoy</span>
        </p>
      </div>
    </section>;
}
export function Results() {
  return <section className="mx-auto max-w-5xl px-4 py-12 md:py-14" data-id="element-89" data-mp="2uqdb9vaudi1o">
      <Eyebrow data-id="element-90" data-mp="2yub9m3sceoto">❤️ Resultados reales</Eyebrow>
      <SectionTitle data-id="element-91" data-mp="2yr5nmxf0g326">
        Corredores que <span className="text-accent" data-id="element-92" data-mp="a1z1rmj8fc27">revolucionaron su capacidad</span>
      </SectionTitle>

      <div className="mt-8 space-y-4" data-id="element-93" data-mp="e2hv5l0rwq6i">
        {developedTestimonials.map(item => <article key={item.name} className="rounded-2xl border border-line bg-white p-5 shadow-sm md:flex md:gap-6 md:p-6" data-id="element-94" data-mp="1ykts7bj0lm3r">
            <div className="flex items-center gap-3 md:w-60 md:shrink-0 md:flex-col md:items-start" data-id="element-95" data-mp="1uk9tw4mpgcg1">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-accent/30 bg-canvas text-sm font-black text-primary" data-id="element-96" data-mp="1pqxac8wvuvu5">
                {item.initials}
              </span>
              <div data-id="element-97" data-mp="1fs7qialff1b9">
                <p className="text-sm font-extrabold text-ink" data-id="element-98" data-mp="1avr35im1wqz8">{item.name}</p>
                <p className="text-xs font-semibold leading-snug text-slate-500" data-id="element-99" data-mp="1axtxgeu8l69r">📍 {item.place}</p>
              </div>
            </div>
            <div className="mt-4 border-t border-line pt-4 md:mt-0 md:flex-1 md:border-l md:border-t-0 md:pl-6 md:pt-0" data-id="element-100" data-mp="11vks03dts338">
              <div className="flex flex-wrap items-center gap-3" data-id="element-101" data-mp="316lrukrdb2nk">
                <span className="text-sm text-gold" data-id="element-102" data-mp="2w8angpjudkcq">⭐⭐⭐⭐⭐</span>
                <span className="rounded-full bg-accent/10 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-accent-dark" data-id="element-103" data-mp="2w6zhcj2xc0ig">
                  {item.result}
                </span>
              </div>
              <p className="mt-3 text-sm font-semibold italic leading-relaxed text-slate-700" data-id="element-104" data-mp="2e5ugebrqf08m">
                “{item.text}”
              </p>
            </div>
          </article>)}
      </div>
    </section>;
}
export function Guarantee() {
  return <section className="mx-auto max-w-5xl px-4 pb-12 md:pb-14" data-id="element-105" data-mp="1lhou3kl1536o">
      <div className="flex flex-col items-center gap-5 rounded-3xl border-2 border-accent/30 bg-white p-6 text-center shadow-sm md:flex-row md:p-8 md:text-left" data-id="element-106" data-mp="1hgd43dy8ufxy">
        <span className="inline-flex shrink-0 rounded-full border-2 border-accent bg-canvas p-4" data-id="element-107" data-mp="1v8te4ypkbe8d">
          <ShieldIcon className="h-9 w-9 text-accent" data-id="element-108" data-mp="1qefak90qy0sm" />
        </span>
        <div data-id="element-109" data-mp="12p325bd8twrd">
          <h3 className="text-lg font-black uppercase text-ink md:text-xl" data-id="element-110" data-mp="12okyhto94g9f">
            Garantía de 7 días, sin preguntas
          </h3>
          <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-111" data-mp="39eelh82qe34y">
            Abrís los tomos y los usás para entrenar. Si no es lo que esperabas, escribís a
            compradigitalyaa@gmail.com y te devolvemos el 100%. Sin formularios, sin drama. Tasa de
            devoluciones: <b className="text-accent-dark" data-id="element-112" data-mp="35d32hled4g5q">menos del 0,5%</b>.
          </p>
        </div>
      </div>
    </section>;
}
export function FAQSection() {
  const [open, setOpen] = useState(0);
  return <section className="border-t border-line bg-white px-4 py-12 md:py-14" data-id="element-113" data-mp="yuukfpj8wt2">
      <div className="mx-auto max-w-3xl" data-id="element-114" data-mp="ws640413bqg">
        <Eyebrow data-id="element-115" data-mp="3j5j2edbyvy2f">
          <span className="inline-flex items-center gap-2" data-id="element-116" data-mp="3j3y7gmlqqd41">
            <HelpCircleIcon className="h-4 w-4" data-id="element-117" data-mp="3o3b2humrmqhp" /> Preguntas frecuentes
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-118" data-mp="27p1ckw2vktor">
          Lo que más <span className="text-accent" data-id="element-119" data-mp="27nzptuchyir7">nos preguntan</span>
        </SectionTitle>
        <div className="mt-8 space-y-3" data-id="element-120" data-mp="2cpp19b3msyft">
          {faqs.map(([question, answer], index) => <div key={question} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-150 ease-out ${open === index ? 'border-accent/60 shadow-lg shadow-slate-200' : 'border-line'}`} data-id="element-121" data-mp="1slhroi9f16cz" data-mpi={index}>
              <button className="flex w-full items-center justify-between gap-3 p-5 text-left text-sm font-extrabold text-ink md:text-base" onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index} data-id="element-122" data-mp="1jrwgta0dg98u">
                {question}
                <ChevronDownIcon className={`h-5 w-5 shrink-0 text-accent transition-transform duration-200 ease-out ${open === index ? 'rotate-180' : ''}`} data-id="element-123" data-mp="10v3j91k68m8p" />
              </button>
              <AnimatePresence initial={false} data-id="element-124" data-mp="2v9o1l7l0s5ox">
                {open === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={collapseTransition} className="border-t border-line px-5 py-4 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-125" data-mp="2q9rwf50l687s">
                    {answer}
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>

        <div className="mt-9 rounded-2xl bg-canvas p-6 text-center" data-id="element-126" data-mp="2ewj5vtk3b0pi">
          <p className="text-base font-black text-ink md:text-xl" data-id="element-127" data-mp="2jt9l6eo2ls9v">
            🎯 El próximo ciclo puede ser diferente.
          </p>
          <p className="mt-2 text-sm font-semibold italic text-accent-dark" data-id="element-128" data-mp="2orkwyuxxecuq">
            “Finalmente un entrenamiento que FUNCIONA.” 🏃
          </p>
          <p className="text-sm font-semibold italic text-accent-dark" data-id="element-129" data-mp="436u350tn548">
            “Finalmente progreso porque tengo un SISTEMA.”
          </p>
          <p className="text-sm font-semibold italic text-accent-dark" data-id="element-130" data-mp="82xhcyr53340">
            “Finalmente sé qué hacer cada semana.”
          </p>
          <div className="mt-5" data-id="element-131" data-mp="1o9truha4tdv">
            <Button href={purchaseUrl} className="w-full text-sm md:w-auto md:text-base" data-id="element-132" data-mp="1p1she66b78d">
              🏃 Quiero mi Combo Running ahora · $19.990 →
            </Button>
          </div>
          <p className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] font-semibold text-slate-500" data-id="element-133" data-mp="blwo9pb2atiy">
            <span className="flex items-center gap-1" data-id="element-134" data-mp="pkwayrf0kych">
              <CheckIcon className="h-3.5 w-3.5 text-accent" data-id="element-135" data-mp="kndzoudiy71b" /> Garantía 7 días
            </span>
            <span className="flex items-center gap-1" data-id="element-136" data-mp="zbqz61btm80k">
              <CheckIcon className="h-3.5 w-3.5 text-accent" data-id="element-137" data-mp="zbh9keuou2ve" /> Acceso en 5 minutos
            </span>
            <span className="flex items-center gap-1" data-id="element-138" data-mp="udfmliu55jen">
              <CheckIcon className="h-3.5 w-3.5 text-accent" data-id="element-139" data-mp="18c7136cx173b" /> Pago 100% seguro
            </span>
          </p>
          <p className="mt-2 text-[11px] font-semibold text-slate-500" data-id="element-140" data-mp="14bwm5475b6cb">
            Mercado Pago · Tarjeta · Transferencia
          </p>
        </div>
      </div>
    </section>;
}